import { ref, computed } from 'vue'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
const KEY_INSTALLED = 'pwa_installed'
const KEY_DISMISSED_AT = 'pwa_dismissed_at'

// Module-level state — shared across all consumers of usePwaInstall().
// initPwaInstall() (called from a Nuxt plugin at app boot) attaches the
// beforeinstallprompt listener early, so the event is captured before any
// component mounts. usePwaInstall() also calls initPwaInstall() lazily as a
// fallback, but on first page load the plugin is what actually wins the race.
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
const dismissedAt = ref<number | null>(null)
const isStandalone = ref(false)
const isIOS = ref(false)
let listenersAttached = false

function readStorageInto() {
  installed.value = localStorage.getItem(KEY_INSTALLED) === 'true'
  const raw = localStorage.getItem(KEY_DISMISSED_AT)
  dismissedAt.value = raw ? Number(raw) : null
}

function detectStandaloneInto() {
  isStandalone.value =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
}

function detectIOSInto() {
  isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent)
}

export function initPwaInstall() {
  if (listenersAttached || typeof window === 'undefined') return
  listenersAttached = true

  readStorageInto()
  detectStandaloneInto()
  detectIOSInto()

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    localStorage.setItem(KEY_INSTALLED, 'true')
    installed.value = true
    deferredPrompt.value = null
  })
}

// Test-only: reset module state so each test starts fresh.
export function _resetPwaInstallForTest() {
  deferredPrompt.value = null
  installed.value = false
  dismissedAt.value = null
  isStandalone.value = false
  isIOS.value = false
  listenersAttached = false
}

export function usePwaInstall() {
  initPwaInstall()

  // Re-read storage and standalone on each invocation in case they
  // changed between mounts (different layout, dismiss action, etc).
  if (typeof window !== 'undefined') {
    readStorageInto()
    detectStandaloneInto()
    detectIOSInto()
  }

  const dismissedWithinWindow = computed(() => {
    if (!dismissedAt.value) return false
    return Date.now() - dismissedAt.value < SEVEN_DAYS_MS
  })

  // Android/desktop: browser fires beforeinstallprompt
  const canInstall = computed(() => {
    return (
      !!deferredPrompt.value &&
      !isStandalone.value &&
      !installed.value &&
      !dismissedWithinWindow.value
    )
  })

  // iOS: no beforeinstallprompt — show manual instructions banner instead
  const canShowIOSBanner = computed(() => {
    return (
      isIOS.value &&
      !isStandalone.value &&
      !installed.value &&
      !dismissedWithinWindow.value
    )
  })

  async function install() {
    const event = deferredPrompt.value
    if (!event) return
    await event.prompt()
    try {
      await event.userChoice
    } catch {
      // ignore
    }
    deferredPrompt.value = null
  }

  function dismiss() {
    const now = Date.now()
    localStorage.setItem(KEY_DISMISSED_AT, String(now))
    dismissedAt.value = now
  }

  return { canInstall, canShowIOSBanner, isIOS, install, dismiss }
}
